  import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { clientMutation } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';
import { ExceptionManager } from 'Core/logManager';

const route = 'TAGS_IMPORT';

// Initial state
const initialState = {
  openImport: false,
  loading: false,
  importResponse: null,
  error: null,
  fileKey: null,
  delimiter: '',
  importNotification: null,
};

// Async thunk for importing tags
export const importTags = createAsyncThunk(
  `${route}/importTags`,
  async (payload, { rejectWithValue }) => {
    try {
      const { fileKey, delimiter } = payload;

      const query = `
        mutation importTags ($fileKey: String!, $delimiter: String!) {
          importTags (fileKey: $fileKey, delimiter: $delimiter) {
            stepFunctionId
            transactionId
            transactionType
            processStartDate
          }
        }
      `;

      const response = await clientMutation(
        query,
        {
          fileKey,
          delimiter,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      if (!response || !response.importTags) {
        throw new Error('Error while importing tags', response.error);
      }

      return response.importTags;
    } catch (exc) {
      ExceptionManager(
        exc,
        'modules/account/views/tags/redux/importSlice',
        'importTags'
      );
      return rejectWithValue(exc.error);
    }
  }
);

// Slice
const tagsImportSlice = createSlice({
  name: route,
  initialState,
  reducers: {
    // Open/close modal actions
    openImportTagsModal: (state, action) => {
      state.openImport = action.payload;
      if (!action.payload) {
        // Reset state when closing modal
        state.importResponse = null;
        state.error = null;
        state.fileKey = null;
        state.importNotification = null;
      }
    },
    
    // Set file key for import
    setFileKeyTag: (state, action) => {
      state.fileKey = action.payload;
    },
    
    // Set delimiter
    setDelimiterTag: (state, action) => {
      state.delimiter = action.payload;
    },

    setImportNotificationTag: (state, action) => {
      // Add validation for notification payload structure
      const notification = action.payload;
      if (notification && typeof notification === 'object') {
        state.importNotification = notification;
      } else {
        state.importNotification = null;
      }
    },
    
    // Reset import state
    resetImportStateTag: (state) => {
      state.loading = false;
      state.importResponse = null;
      state.error = null;
      state.fileKey = null;
      state.delimiter = '';
      state.importNotification = null;
    },
    
    // Clear error
    clearErrorTag: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Import tags pending
      .addCase(importTags.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.importResponse = null;
      })
      // Import tags fulfilled
      .addCase(importTags.fulfilled, (state, action) => {
        state.loading = false;
        state.importResponse = action.payload;
        state.error = null;
      })
      // Import tags rejected
      .addCase(importTags.rejected, (state, action) => {
        state.loading = false;
        state.importResponse = null;
        state.error = action.payload || 'Import failed';
      });
  },
});

export const { actions, reducer } = tagsImportSlice;

export const { 
  openImportTagsModal, 
  setFileKeyTag, 
  setDelimiterTag, 
  resetImportStateTag, 
  clearErrorTag, 
  setImportNotificationTag
} = actions;

export default reducer;